import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Divider,
  HStack,
  Heading,
  ProgressBar,
  Text,
  VStack,
} from '@astryxdesign/core';

import { formatNumber, formatQuota, type UsageResponse } from '../lib/api';
import { $auth } from '../stores/auth';
import { $usage, $usageError, loadUsage } from '../stores/usage';
import { $keys, loadKeys } from '../stores/keys';

export function OverviewPage() {
  const auth = useStore($auth);
  const usage = useStore($usage);
  const usageError = useStore($usageError);
  const keys = useStore($keys);

  useEffect(() => {
    void loadUsage(30);
    void loadKeys();
  }, []);

  const activeKeys = keys.filter((k) => !k.revoked).length;
  const plan = auth.plan;

  return (
    <VStack gap={3}>
      <VStack gap={0.5}>
        <Heading level={1}>Overview</Heading>
        <Text color="secondary">
          {auth.user?.name ? `Welcome back, ${auth.user.name}.` : auth.user?.email}
        </Text>
      </VStack>

      {usageError && <Banner status="error" title="Could not load usage" description={usageError} />}

      {activeKeys === 0 && (
        <Banner
          status="info"
          title="You have no API keys yet"
          description="Create a key to start calling the forecast endpoint."
          endContent={<Button label="Create a key" variant="primary" size="sm" href="/app/keys" />}
        />
      )}

      <div className="stat-grid">
        <Stat label="Plan" value={plan?.name ?? '—'} hint={plan ? `${plan.rate_limit_rpm} req/min` : ''} />
        <Stat
          label="Calls this month"
          value={usage ? formatNumber(usage.current.requests) : '—'}
          hint={plan ? `of ${formatQuota(plan.monthly_quota)}` : ''}
        />
        <Stat
          label="Forecast points"
          value={usage ? formatNumber(usage.current.forecast_points) : '—'}
          hint="series × horizon"
        />
        <Stat label="Active keys" value={String(activeKeys)} hint={plan ? `max ${plan.max_keys}` : ''} />
        <Stat
          label="Throttled"
          value={usage ? formatNumber(usage.current.throttled_count) : '—'}
          hint="429 responses"
        />
        <Stat
          label="Avg inference"
          value={usage ? avgInference(usage) : '—'}
          hint="model time per call"
        />
      </div>

      {usage && plan && plan.monthly_quota > 0 && (
        <Card padding={3} variant="default">
          <VStack gap={1.5}>
            <HStack gap={1} justify="between" vAlign="center" wrap="wrap">
              <Text weight="semibold">Monthly quota</Text>
              <Badge
                label={`${usage.quota_used_pct}% used`}
                variant={usage.quota_used_pct >= 90 ? 'error' : usage.quota_used_pct >= 70 ? 'warning' : 'neutral'}
              />
            </HStack>
            <ProgressBar
              label="Monthly forecast calls"
              isLabelHidden
              value={Math.min(usage.current.requests, plan.monthly_quota)}
              max={plan.monthly_quota}
              variant={usage.quota_used_pct >= 90 ? 'error' : 'accent'}
            />
            <Text size="sm" color="secondary">
              {formatNumber(usage.current.requests)} of {formatNumber(plan.monthly_quota)} calls ·{' '}
              {formatNumber(usage.remaining)} remaining · resets{' '}
              {new Date(usage.period_ends_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </Text>
          </VStack>
        </Card>
      )}

      <Card padding={3} variant="default">
        <VStack gap={2}>
          <Text weight="semibold">Next steps</Text>
          <Divider variant="subtle" />
          <VStack gap={1.5}>
            <StepRow
              title="Create an API key"
              body="Keys are shown once. Store them in your secret manager, not in source control."
              action={<Button label="API keys" variant="secondary" size="sm" href="/app/keys" />}
            />
            <StepRow
              title="Try a forecast in the browser"
              body="The playground runs on your logged-in session — no key needed. It spends real quota."
              action={<Button label="Playground" variant="secondary" size="sm" href="/app/playground" />}
            />
            <StepRow
              title="Wire it into your job"
              body="One POST per batch of series. Copy-paste examples for Python and TypeScript."
              action={<Button label="API docs" variant="secondary" size="sm" href="/docs" />}
            />
          </VStack>
        </VStack>
      </Card>
    </VStack>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card padding={3} variant="default">
      <VStack gap={0.5}>
        <Text size="sm" color="secondary">
          {label}
        </Text>
        <Heading level={3}>{value}</Heading>
        {hint ? (
          <Text size="xsm" color="secondary">
            {hint}
          </Text>
        ) : null}
      </VStack>
    </Card>
  );
}

function StepRow({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <HStack gap={2} justify="between" vAlign="center" wrap="wrap">
      <VStack gap={0.5}>
        <Text size="sm" weight="medium">
          {title}
        </Text>
        <Text size="sm" color="secondary">
          {body}
        </Text>
      </VStack>
      {action}
    </HStack>
  );
}

function avgInference(usage: UsageResponse): string {
  const { requests, inference_millis: ms } = usage.current;
  if (requests === 0) return '—';
  return `${Math.round(ms / requests)} ms`;
}
